import { StyledReactSelect } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';

import { useStyles } from './styles';

/**
 * One entry in a set-membership picker.
 *
 * Deliberately not `ConditionSelectOption`, which also carries `isDisabled` for
 * the condition-type picker. Nothing disables an entity, and borrowing a type
 * with a field this control drops would let a caller set one that silently does
 * nothing.
 */
export interface ConditionMultiSelectOption {
  value: string;
  /** Falls back to the value — a funnel's title is nullable. */
  label?: string;
}

export interface ConditionMultiSelectProps {
  /** Lands on the input, so the row's controls stay individually addressable. */
  id: string;
  label: string;
  /** What the condition holds, in the order it holds it. */
  values: string[];
  options: ConditionMultiSelectOption[];
  onChange: (values: string[]) => void;
  /** The message for this control, once the admin has tried to save. */
  error?: string;
  /**
   * Said when the server's cap cut the option list short, so a search over a
   * partial list is never read as a search over the account.
   */
  note?: string;
  /** The control's place in the row, which the row decides rather than this. */
  className?: string;
}

/**
 * One set-membership picker in a condition row — positions, openings, locations.
 *
 * The multi sibling of `ConditionSelect`, and configured the same way for the
 * same reasons: `size="small"` to match the row's other control, no `required`
 * asterisk on a row where every control is, an empty placeholder because the
 * label already names the box. Read that component's notes for those three.
 *
 * What is specific to holding a set:
 *
 * - `isClearable`, which is the design's clear-all. react-select turns it on for
 *   a multi by default; it is stated here because it is a requirement rather
 *   than an inheritance.
 * - `hideSelectedOptions={false}`. react-select drops a chosen option out of the
 *   menu for a multi, so the list shortens as you pick and the same row cannot be
 *   used to unpick. These lists are an account's own positions and locations, and
 *   a list that rearranges itself under the pointer is the worse trade.
 * - A stored value the catalog does not list still becomes a chip. The server
 *   caps each list and openings are active ones only, so a rule scoped to an
 *   opening that has since closed would otherwise lose that scope the moment
 *   anyone opened the rule — silently widening a compliance gate on save.
 *
 * Both messages are wired to the input with `aria-describedby` rather than
 * rendered beside it: the design system passes that attribute through to the
 * real input (AXHE-4666), so the note and the error are announced with the
 * control instead of being read only by someone who happens to move past them.
 */
export const ConditionMultiSelect: React.FC<ConditionMultiSelectProps> = ({
  id,
  label,
  values,
  options,
  onChange,
  error,
  note,
  className,
}) => {
  const styles = useStyles();

  // Memoised on the caller's array for the reason `ConditionSelect` gives:
  // react-select takes a `setState` whenever `options` changes by identity, and
  // the panel re-renders on every keystroke elsewhere in the row.
  const resolved = React.useMemo(
    () =>
      options.map(option => ({
        value: option.value,
        label: option.label ?? option.value,
      })),
    [options],
  );

  /**
   * One chip per distinct value, in the order the condition holds them.
   *
   * The de-duplication is not cosmetic. react-select identifies a value by its
   * `value`, so `removeValue` filters *every* entry matching the one clicked — a
   * condition storing the same position twice would render two chips and lose
   * both to one X. A condition is a set membership test, so the second copy was
   * never meaning anything; showing one chip is what the rule actually does, and
   * it makes the X remove exactly what it appears to.
   *
   * Nothing in this builder can write a duplicate, so this only ever reads one
   * that arrived another way — an older rule, an API write, an import.
   */
  const selected = React.useMemo(
    () =>
      Array.from(new Set(values)).map(
        value =>
          resolved.find(option => option.value === value) ?? {
            value,
            // The stored id or name, which is a worse label than the record's own
            // and still says which record. `id_options` ships a null label for a
            // funnel with no title, so the catalog does this too.
            label: value,
          },
      ),
    [resolved, values],
  );

  const errorId = `${id}-error`;
  const noteId = `${id}-note`;
  const describedBy = [error && errorId, note && noteId].filter(Boolean).join(' ');

  return (
    <div className={clsx(styles.conditionSelect, className)}>
      <StyledReactSelect
        inputId={id}
        label={label}
        // Both, for the reason `ConditionSelect` passes both: the design system
        // names the control from the rendered label only when the caller names
        // nothing itself, and several selects in this app pass `label` alone and
        // are unnamed to a screen reader.
        aria-label={label}
        aria-describedby={describedBy || undefined}
        options={resolved}
        value={selected}
        onChange={(next?: ConditionMultiSelectOption[] | null) =>
          onChange((next ?? []).map(option => option.value))
        }
        // Boolean, not the message: a string `error` renders the design system's
        // own caption 24px below the control, which on a row this dense lands on
        // the control underneath. The border still turns, and the message is
        // rendered in flow below.
        error={Boolean(error)}
        isClearable
        isMulti
        isSearchable
        hideSelectedOptions={false}
        tabSelectsValue={false}
        placeholder=""
        size="small"
      />

      {note && (
        <Typography className={styles.conditionSelectNote} id={noteId} variant="body2">
          {note}
        </Typography>
      )}

      {/* `role="alert"` for the reason `ConditionSelect`'s message carries it:
          this is mounted only once a save has been refused, so its appearance is
          the event to announce, and a refused save moves no focus. */}
      {error && (
        <Typography
          className={styles.conditionSelectError}
          color="error"
          id={errorId}
          role="alert"
          variant="body2"
        >
          {error}
        </Typography>
      )}
    </div>
  );
};
